import { detectWebCapabilities } from "./capabilities.js";
/** Chunk interval. Small enough that a crash loses little, large enough not to
 * churn the disk. */
const TIMESLICE_MS = 2000;
/** Below this there is not enough room for even a brief clip, so starting would
 * only produce a stub. Roughly 15 seconds of live TV at 5 Mbps. */
const MIN_START_BYTES = 10 * 1024 * 1024;
/** Target quality for the re-encode. Roughly matches a 720p provider stream;
 * the encoder backs off if the device cannot sustain it. */
const VIDEO_BITRATE_BPS = 4_000_000;
const AUDIO_BITRATE_BPS = 128_000;
/** Human-readable size for messages the user actually reads. */
export function formatBytes(bytes) {
    if (bytes >= 1024 ** 3)
        return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
    if (bytes >= 1024 ** 2)
        return `${Math.round(bytes / 1024 ** 2)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
}
/** "1 h 20 min" / "35 min" / "40 sec" — for "you can record about X". */
export function formatDuration(seconds) {
    if (seconds >= 3600) {
        const h = Math.floor(seconds / 3600);
        const m = Math.round((seconds % 3600) / 60);
        return m > 0 ? `${h} h ${m} min` : `${h} h`;
    }
    if (seconds >= 60)
        return `${Math.round(seconds / 60)} min`;
    return `${Math.round(seconds)} sec`;
}
/**
 * Browser recording engine.
 *
 * Chunks are written to the sink AS THEY ARRIVE rather than accumulated and
 * saved at the end. Holding a whole recording in memory is what turns "record
 * the match" into a dead tab: a couple of hours at a few Mbps is gigabytes, and
 * the device is already spending memory on playback.
 *
 * This engine RE-ENCODES — MediaRecorder has no copy mode — so it costs CPU on
 * a device that is simultaneously decoding. That is inherent to the browser,
 * and is why `capabilities.streamCopy` is false and why the native engine
 * exists.
 */
export class WebRecorder {
    capabilities;
    video;
    now;
    createRecorder;
    openSink;
    listeners = new Map();
    recorder = null;
    sink = null;
    entry = null;
    stopTimer = null;
    disposed = false;
    constructor(options) {
        this.video = options.video;
        this.now = options.now ?? Date.now;
        this.capabilities = options.capabilities ?? detectWebCapabilities();
        this.createRecorder =
            options.createRecorder ??
                ((stream, opts) => new MediaRecorder(stream, opts));
        this.openSink = options.openSink ?? openOpfsSink;
    }
    current() {
        return this.entry;
    }
    async storage() {
        let quotaBytes;
        let usedBytes;
        try {
            const est = await navigator.storage?.estimate?.();
            quotaBytes = typeof est?.quota === "number" ? est.quota : undefined;
            usedBytes = typeof est?.usage === "number" ? est.usage : undefined;
        }
        catch {
            // Some engines refuse to estimate; treat that as "unknown", never as zero.
        }
        const availableBytes = quotaBytes !== undefined && usedBytes !== undefined ? Math.max(0, quotaBytes - usedBytes) : undefined;
        return {
            ...(quotaBytes !== undefined ? { quotaBytes } : {}),
            ...(usedBytes !== undefined ? { usedBytes } : {}),
            ...(availableBytes !== undefined ? { availableBytes } : {}),
            estimatedSecondsAt(bitrateBps) {
                if (availableBytes === undefined || bitrateBps <= 0)
                    return undefined;
                return Math.floor((availableBytes * 8) / bitrateBps);
            },
        };
    }
    async start(request) {
        if (this.disposed)
            throw fail("unsupported", "This recorder has been disposed.");
        if (!this.capabilities.canRecord) {
            throw fail("unsupported", this.capabilities.unavailableReason ?? "Recording is not supported here.");
        }
        if (this.entry && this.entry.status === "recording") {
            throw fail("capture_failed", "A recording is already running.");
        }
        // A stream captured from a PAUSED or not-yet-ready element yields no
        // frames, and MediaRecorder does not recover when playback later starts:
        // measured 36 bytes over 12s that way, against 124 KB in 5s from a playing
        // element. Refuse with something the user can act on rather than producing
        // an empty file that looks like a successful recording.
        if (this.video.readyState < 2 || this.video.paused) {
            throw fail("capture_failed", "Start playback before recording.");
        }
        const startedAt = new Date(this.now()).toISOString();
        const entry = {
            id: `rec-${this.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
            contentRef: request.contentRef,
            title: request.title,
            ...(request.channelName ? { channelName: request.channelName } : {}),
            startedAt,
            bytes: 0,
            status: "starting",
            container: this.capabilities.container,
            engine: "web",
        };
        this.entry = entry;
        this.emit("statechange", entry);
        let stream;
        try {
            const capture = this.video;
            const fn = capture.captureStream ?? capture.mozCaptureStream;
            if (!fn)
                throw new Error("captureStream unavailable");
            stream = fn.call(capture);
        }
        catch {
            return this.abort(entry, fail("capture_failed", "Could not capture video from the player."));
        }
        try {
            this.sink = await this.openSink(fileNameFor(request, this.capabilities.container, startedAt));
        }
        catch {
            return this.abort(entry, fail("write_failed", "Could not open a file to record into."));
        }
        // Free-space policy, checked AFTER the sink is known because it depends on
        // where the bytes are going.
        //
        // An external sink is not gated at all: the user chose a real location and
        // its capacity is the drive's, not the browser's quota. Sparkle works the
        // same way — it gates on internal storage size but lets any device record
        // once an external drive is attached, which is what makes a cheap TV stick
        // usable.
        //
        // For the OPFS sink, storage is a QUANTITY rather than a capability, so it
        // is checked here rather than hiding the control: the user can free space,
        // and a short clip succeeds where an hour would not. Refuse only below a
        // floor where even a brief recording is pointless, and name the limit.
        if (!this.sink.external) {
            const space = await this.storage();
            if (space.availableBytes !== undefined && space.availableBytes < MIN_START_BYTES) {
                await this.sink.close().catch(() => undefined);
                this.sink = null;
                return this.abort(entry, fail("storage_full", `Storage limited — about ${formatBytes(space.availableBytes)} free on this device, ` +
                    "which is not enough to start. Free some space, or record to an external drive."));
            }
        }
        // MediaRecorder's default bitrate is very low — measured ~200 kbps on a
        // 720p source, which looks like mush next to the stream being recorded.
        // Ask for something proportionate; the encoder reduces it if it cannot
        // keep up, which is preferable to silently recording something unwatchable.
        const recorder = this.createRecorder(stream, {
            mimeType: this.capabilities.container,
            videoBitsPerSecond: VIDEO_BITRATE_BPS,
            audioBitsPerSecond: AUDIO_BITRATE_BPS,
        });
        this.recorder = recorder;
        recorder.ondataavailable = (event) => {
            if (!event.data || event.data.size === 0 || !this.sink || !this.entry)
                return;
            // Write-through, never buffer the whole recording in memory.
            void this.sink
                .write(event.data)
                .then(() => {
                if (!this.entry)
                    return;
                this.entry = { ...this.entry, bytes: this.entry.bytes + event.data.size };
                this.emit("progress", this.entry);
            })
                .catch(() => {
                // A failed write is usually a full disk. Stop rather than silently
                // producing a truncated file the user thinks is complete.
                // Keep what was already written and label it, rather than discarding
                // it or leaving a truncated file the user believes is complete.
                void this.stop().catch(() => undefined);
                this.emitError(fail("storage_full", "Recording stopped early — the device ran out of space. What was recorded up to that point has been kept."));
            });
        };
        recorder.onerror = () => {
            void this.stop().catch(() => undefined);
            this.emitError(fail("capture_failed", "Recording stopped unexpectedly."));
        };
        try {
            recorder.start(TIMESLICE_MS);
        }
        catch {
            return this.abort(entry, fail("capture_failed", "This browser refused to start recording."));
        }
        this.entry = { ...entry, status: "recording", locator: this.sink.locator };
        this.emit("statechange", this.entry);
        if (request.maxDurationSeconds && request.maxDurationSeconds > 0) {
            this.stopTimer = setTimeout(() => void this.stop().catch(() => undefined), request.maxDurationSeconds * 1000);
        }
        return this.entry;
    }
    async stop() {
        if (this.stopTimer) {
            clearTimeout(this.stopTimer);
            this.stopTimer = null;
        }
        const recorder = this.recorder;
        const entry = this.entry;
        if (!recorder || !entry || entry.status !== "recording")
            return entry;
        this.entry = { ...entry, status: "finalizing" };
        this.emit("statechange", this.entry);
        await new Promise((resolve) => {
            recorder.onstop = () => resolve();
            try {
                recorder.stop();
            }
            catch {
                resolve();
            }
        });
        try {
            await this.sink?.close();
        }
        catch {
            // The bytes already written are still there; a close failure should not
            // lose the recording that exists.
        }
        this.recorder = null;
        this.sink = null;
        const done = {
            ...this.entry,
            status: "done",
            endedAt: new Date(this.now()).toISOString(),
        };
        this.entry = done;
        this.emit("statechange", done);
        this.emit("done", done);
        return done;
    }
    on(event, handler) {
        const set = this.listeners.get(event) ?? new Set();
        set.add(handler);
        this.listeners.set(event, set);
        return () => set.delete(handler);
    }
    dispose() {
        this.disposed = true;
        if (this.stopTimer)
            clearTimeout(this.stopTimer);
        this.stopTimer = null;
        try {
            if (this.recorder && this.recorder.state !== "inactive")
                this.recorder.stop();
        }
        catch {
            // Disposing must never throw.
        }
        this.recorder = null;
        void this.sink?.close().catch(() => undefined);
        this.sink = null;
        this.listeners.clear();
    }
    abort(entry, error) {
        this.entry = { ...entry, status: "error", error };
        this.emit("statechange", this.entry);
        this.emitError(error);
        throw error;
    }
    emit(event, payload) {
        for (const handler of this.listeners.get(event) ?? []) {
            handler(payload);
        }
    }
    emitError(error) {
        this.emit("error", error);
    }
}
function fail(code, message) {
    return { code, message };
}
/** Filesystem-safe name carrying enough to identify the recording later. */
export function fileNameFor(request, container, startedAt) {
    const ext = container.includes("mp4") ? "mp4" : container.includes("mp2t") ? "ts" : "webm";
    const stamp = startedAt.replace(/[:.]/g, "-");
    // Callers often have only one name for a live channel, so joining a channel
    // and title that are identical produced "HBO-Boxing-HBO-Boxing".
    const sameName = request.channelName && request.channelName.trim() === request.title.trim();
    const safe = (request.channelName && !sameName ? `${request.channelName}-${request.title}` : request.title)
        .replace(/[^\p{L}\p{N}\-_ ]/gu, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 80);
    return `${safe || "recording"}-${stamp}.${ext}`;
}
/**
 * External sink: the user picks a real file, which may live on an attached
 * drive. This is the browser's only route to storage that is not a quota, and
 * it exists on desktop Chromium only — Android/Fire TV WebViews and iOS do not
 * expose it, which is precisely why DVR-to-USB on a TV box is native-shell
 * work rather than something the web build can promise.
 *
 * Must be called from a user gesture, or the picker is blocked.
 */
export async function openExternalSink(fileName) {
    const picker = globalThis
        .showSaveFilePicker;
    if (typeof picker !== "function") {
        throw new Error("showSaveFilePicker unavailable");
    }
    const ext = fileName.slice(fileName.lastIndexOf("."));
    const handle = await picker({
        suggestedName: fileName,
        types: [{ description: "Recording", accept: { "video/*": [ext] } }],
    });
    const writable = await handle.createWritable();
    return {
        locator: `file:${handle.name}`,
        // A location the user chose: its capacity is the drive's, so the OPFS
        // quota floor does not apply.
        external: true,
        async write(chunk) {
            await writable.write(chunk);
        },
        async close() {
            await writable.close();
        },
    };
}
/** Default sink: OPFS. Sandboxed and quota-limited, but present in every
 * browser that can record at all. External storage needs the native engine. */
async function openOpfsSink(fileName) {
    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle("recordings", { create: true });
    const file = await dir.getFileHandle(fileName, { create: true });
    const writable = await file.createWritable();
    return {
        locator: `opfs:/recordings/${fileName}`,
        // The browser quota, not a real disk — so the free-space floor applies.
        external: false,
        async write(chunk) {
            await writable.write(chunk);
        },
        async close() {
            await writable.close();
        },
    };
}
//# sourceMappingURL=web.js.map